import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import { useUser } from "@/app/session.tsx";
import { TopBar } from "@/components/TopBar.tsx";
import { Button, EmptyState, Input, Panel } from "@/components/primitives.tsx";
import { formatCount } from "@/i18n/index.ts";
import { ApiError, api } from "@/lib/api.ts";
import type { Collection } from "@/lib/api-types.ts";

export function CollectionsRoute() {
  const { t, i18n } = useTranslation();
  const user = useUser();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Collection | null>(null);

  const collections = useQuery({ queryKey: ["collections"], queryFn: api.collections });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteCollection(id),
    onSuccess: () => {
      setConfirmDelete(null);
      void queryClient.invalidateQueries({ queryKey: ["collections"] });
    },
  });

  return (
    <div className="lib">
      <TopBar />
      <div className="wrap">
        <div className="row" style={{ marginBottom: "var(--s2)" }}>
          <div>
            <h1 className="page-title">{t("collections.title")}</h1>
            <p className="page-sub">{t("collections.subtitle")}</p>
          </div>
          <span className="spacer" />
          <Button variant="primary" onClick={() => setCreating(true)}>
            {t("collections.create")}
          </Button>
        </div>

        {collections.data && collections.data.length === 0 ? (
          <EmptyState
            title={t("collections.emptyTitle")}
            body={t("collections.emptyBody")}
            action={
              <Button variant="primary" onClick={() => setCreating(true)}>
                {t("collections.create")}
              </Button>
            }
          />
        ) : null}

        {/* THE WHOLE CARD IS THE TARGET. The link used to wrap the title alone, so a
            card-sized thing that plainly looks clickable had a text-sized hit area,
            and every first-time reader concluded it was broken. The anchor is
            stretched over the card with `.stretch-link::after`; the delete button
            sits above it on its own stacking context, so it still works. */}
        <div className="cards">
          {collections.data?.map((collection) => (
            <article key={collection.id} className="coll-card" style={{ position: "relative" }}>
              <Link
                to="/collections/$collectionId"
                params={{ collectionId: collection.id }}
                className="stretch-link"
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <div className="card-title">{collection.name}</div>
              </Link>
              {collection.description ? (
                <p className="page-sub" style={{ margin: "4px 0 var(--s3)" }}>
                  {collection.description}
                </p>
              ) : null}

              <div className="row" style={{ flexWrap: "wrap" }}>
                <span className="faint">
                  {/* document_count counts DOCUMENT ROWS, so a queued upload counts 0.
                      Saying "1 dokumen" for a file that cannot be retrieved would be
                      a lie the user would only discover by asking a question. */}
                  {collection.document_count === 0
                    ? t("collections.documentCountZero")
                    : t("collections.documentCount", {
                        count: collection.document_count,
                      })}
                </span>
                {collection.confidential ? (
                  <span className="pill pill-amber">{t("collections.confidential")}</span>
                ) : null}
                {collection.owning_org_unit ? (
                  <span className="pill pill-bare">{collection.owning_org_unit}</span>
                ) : null}
              </div>
              <p className="hint" style={{ marginTop: 4 }}>
                {t("collections.pendingNote")}
              </p>

              <div className="row" style={{ marginTop: "var(--s3)" }}>
                <span className="spacer" />
                <Button
                  size="sm"
                  onClick={() => setConfirmDelete(collection)}
                  // The API admin-gates DELETE only when require_auth is ON, but the
                  // UI states the rule that will apply, rather than offering a button
                  // whose availability changes with a server flag.
                  disabled={user.role !== "admin"}
                  title={user.role !== "admin" ? t("collections.adminOnly") : undefined}
                >
                  {t("collections.delete")}
                </Button>
              </div>
              <span className="sr-only">
                {formatCount(collection.document_count, i18n.language)}
              </span>
            </article>
          ))}
        </div>
      </div>

      {creating ? (
        <CreateCollectionDialog
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ["collections"] });
          }}
        />
      ) : null}

      {confirmDelete ? (
        <div className="scrim open" role="dialog" aria-modal="true" aria-label={t("collections.delete")}>
          <div className="panel panel-red" style={dialogStyle}>
            <div className="panel-title">
              {t("collections.deleteConfirm", { name: confirmDelete.name })}
            </div>
            {/* A collection is a LABEL. Deleting it must not read as deleting its
                documents, because it does not — they lose the label and stay searchable. */}
            <div className="panel-body">{t("collections.deleteBody")}</div>
            {remove.error ? (
              <p className="hint" style={{ color: "var(--red)" }}>
                {remove.error instanceof ApiError ? remove.error.message : t("errors.generic")}
              </p>
            ) : null}
            <div className="row" style={{ marginTop: "var(--s4)" }}>
              <span className="spacer" />
              <Button onClick={() => setConfirmDelete(null)}>{t("collections.cancel")}</Button>
              <Button
                variant="primary"
                onClick={() => remove.mutate(confirmDelete.id)}
                disabled={remove.isPending}
              >
                {t("collections.deleteSubmit")}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const dialogStyle: React.CSSProperties = {
  background: "var(--surface)",
  maxWidth: 460,
  margin: "12vh auto 0",
  boxShadow: "0 24px 60px rgb(20 22 26 / 0.22)",
};

function CreateCollectionDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [orgUnit, setOrgUnit] = useState("");
  const [positionLevel, setPositionLevel] = useState("");
  const [confidential, setConfidential] = useState(false);

  const create = useMutation({
    mutationFn: () =>
      api.createCollection({
        name: name.trim(),
        description: description.trim() || undefined,
        owning_org_unit: orgUnit.trim() || undefined,
        // Sent only when it is a real integer. An empty string would be a 422, and a
        // zero would be a real ABAC floor the user did not ask for.
        min_position_level: positionLevel.trim() === "" ? undefined : Number(positionLevel),
        confidential,
      }),
    onSuccess: onCreated,
  });

  const conflict = create.error instanceof ApiError && create.error.status === 409;

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    create.mutate();
  }

  return (
    <div className="scrim open" role="dialog" aria-modal="true" aria-label={t("collections.createTitle")}>
      <form className="panel panel-accent" style={dialogStyle} onSubmit={onSubmit}>
        <div className="panel-title">{t("collections.createTitle")}</div>

        <div className="stack" style={{ marginTop: "var(--s3)" }}>
          <label className="stack" style={{ gap: 4 }}>
            <span className="eyebrow">{t("collections.name")}</span>
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t("collections.namePlaceholder")}
              required
              autoFocus
            />
          </label>

          <label className="stack" style={{ gap: 4 }}>
            <span className="eyebrow">{t("collections.description")}</span>
            <Input
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder={t("collections.descriptionPlaceholder")}
            />
          </label>

          <label className="stack" style={{ gap: 4 }}>
            <span className="eyebrow">{t("collections.orgUnit")}</span>
            <Input value={orgUnit} onChange={(event) => setOrgUnit(event.target.value)} />
            <span className="hint">{t("collections.orgUnitHelp")}</span>
          </label>

          <label className="stack" style={{ gap: 4 }}>
            <span className="eyebrow">{t("collections.positionLevel")}</span>
            <Input
              type="number"
              min={0}
              step={1}
              value={positionLevel}
              onChange={(event) => setPositionLevel(event.target.value)}
            />
          </label>

          <label className="row">
            <input
              type="checkbox"
              checked={confidential}
              onChange={(event) => setConfidential(event.target.checked)}
            />
            <span>{t("collections.confidential")}</span>
          </label>
          <span className="hint">{t("collections.confidentialHelp")}</span>
        </div>

        {create.error ? (
          <Panel tone="red" title={conflict ? t("collections.nameTaken") : t("errors.generic")}>
            {conflict ? null : create.error instanceof ApiError ? create.error.message : null}
          </Panel>
        ) : null}

        <div className="row" style={{ marginTop: "var(--s4)" }}>
          <span className="spacer" />
          <Button onClick={onClose}>{t("collections.cancel")}</Button>
          <Button type="submit" variant="primary" disabled={create.isPending || !name.trim()}>
            {create.isPending ? t("collections.submitting") : t("collections.submit")}
          </Button>
        </div>
      </form>
    </div>
  );
}
